/**
 * ChatbotQualitySection Component
 * Displays AI-generated quality analysis for chatbot conversations
 */

import { useChatbotQuality } from '@/hooks/useAnalytics';
import type { AnalyticsDateRangeParams } from '@/types/analytics';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, TrendingUp, CheckCircle, AlertCircle, XCircle, Zap, Hash, Smile, Meh, Frown } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

interface ChatbotQualitySectionProps {
  dateRange: AnalyticsDateRangeParams;
}

export function ChatbotQualitySection({ dateRange }: ChatbotQualitySectionProps) {
  const { data, isLoading, error } = useChatbotQuality(dateRange);

  if (error) {
    return (
      <div className="p-4 border border-destructive rounded-lg bg-destructive/10">
        <p className="text-sm text-destructive">Failed to load chatbot quality analysis: {error.message}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-32 bg-muted animate-pulse rounded" />
        <div className="h-48 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  const summary = data?.summary || {
    totalAnalyses: 0,
    avgQualityScore: 0,
    resolutionDistribution: { resolved: 0, escalated: 0, abandoned: 0 },
    engagementLevels: { high: 0, medium: 0, low: 0 },
    sentimentBreakdown: { positive: 0, neutral: 0, negative: 0 },
  };

  // Get top topics (aggregate from all analyses)
  const topTopics = data?.analyses
    ? data.analyses
        .flatMap((analysis) => analysis.key_topics)
        .reduce((acc, topic) => {
          acc[topic] = (acc[topic] || 0) + 1;
          return acc;
        }, {} as Record<string, number>)
    : {};

  const topTopicsList = Object.entries(topTopics)
    .sort(([, countA], [, countB]) => countB - countA)
    .slice(0, 8);

  // Get top recommendations
  const topRecommendations = data?.analyses
    ? data.analyses
        .flatMap((analysis) => analysis.recommendations)
        .reduce((acc, rec) => {
          acc[rec] = (acc[rec] || 0) + 1;
          return acc;
        }, {} as Record<string, number>)
    : {};

  const topRecommendationsList = Object.entries(topRecommendations)
    .sort(([, countA], [, countB]) => countB - countA)
    .slice(0, 5);

  const getSentimentIcon = (sentiment: 'positive' | 'neutral' | 'negative') => {
    switch (sentiment) {
      case 'positive':
        return <Smile className="h-4 w-4 text-green-600" />;
      case 'neutral':
        return <Meh className="h-4 w-4 text-yellow-600" />;
      case 'negative':
        return <Frown className="h-4 w-4 text-red-600" />;
    }
  };

  const getResolutionIcon = (status: string) => {
    switch (status) {
      case 'resolved':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'escalated':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case 'abandoned':
        return <XCircle className="h-4 w-4 text-red-600" />;
    }
  };

  const getEngagementIcon = (level: string) => {
    switch (level) {
      case 'high':
        return <Zap className="h-4 w-4 text-green-600" />;
      case 'medium':
        return <Zap className="h-4 w-4 text-yellow-600" />;
      case 'low':
        return <Zap className="h-4 w-4 text-red-600" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Avg Quality Score */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Avg Quality Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{summary.avgQualityScore.toFixed(1)}</p>
            <Progress value={summary.avgQualityScore} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-1">Out of 100</p>
          </CardContent>
        </Card>

        {/* Total Analyses */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Total Analyses
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{summary.totalAnalyses}</p>
            <p className="text-xs text-muted-foreground mt-2">AI-analyzed conversations</p>
          </CardContent>
        </Card>

        {/* Resolution Rate */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Resolution Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">
              {summary.totalAnalyses > 0
                ? ((summary.resolutionDistribution.resolved / summary.totalAnalyses) * 100).toFixed(1)
                : '0'}
              %
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {summary.resolutionDistribution.resolved} resolved conversations
            </p>
          </CardContent>
        </Card>

        {/* High Engagement Rate */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">High Engagement</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">
              {summary.totalAnalyses > 0
                ? ((summary.engagementLevels.high / summary.totalAnalyses) * 100).toFixed(1)
                : '0'}
              %
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {summary.engagementLevels.high} highly engaged leads
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Resolution Status Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Resolution Status</CardTitle>
          <CardDescription>Breakdown of conversation outcomes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(summary.resolutionDistribution).map(([status, count]) => {
              const percentage = summary.totalAnalyses > 0 ? (count / summary.totalAnalyses) * 100 : 0;
              return (
                <div key={status} className="flex items-center gap-3">
                  {getResolutionIcon(status)}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium capitalize">{status}</span>
                      <span className="text-sm text-muted-foreground">
                        {count} ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Engagement Levels & Sentiment */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Engagement Levels */}
        <Card>
          <CardHeader>
            <CardTitle>Engagement Levels</CardTitle>
            <CardDescription>Lead engagement distribution</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(summary.engagementLevels).map(([level, count]) => {
                const percentage = summary.totalAnalyses > 0 ? (count / summary.totalAnalyses) * 100 : 0;
                return (
                  <div key={level} className="flex items-center gap-3">
                    {getEngagementIcon(level)}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium capitalize">{level}</span>
                        <span className="text-sm text-muted-foreground">
                          {count} ({percentage.toFixed(1)}%)
                        </span>
                      </div>
                      <Progress value={percentage} className="h-2" />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Sentiment Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Sentiment Breakdown</CardTitle>
            <CardDescription>Lead sentiment analysis</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(summary.sentimentBreakdown).map(([sentiment, count]) => {
                const percentage = summary.totalAnalyses > 0 ? (count / summary.totalAnalyses) * 100 : 0;
                return (
                  <div key={sentiment} className="flex items-center gap-3">
                    {getSentimentIcon(sentiment as 'positive' | 'neutral' | 'negative')}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium capitalize">{sentiment}</span>
                        <span className="text-sm text-muted-foreground">
                          {count} ({percentage.toFixed(1)}%)
                        </span>
                      </div>
                      <Progress value={percentage} className="h-2" />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Topics & Recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Topics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Hash className="h-5 w-5 text-blue-600" />
              Top Topics
            </CardTitle>
            <CardDescription>Most discussed topics in conversations</CardDescription>
          </CardHeader>
          <CardContent>
            {topTopicsList.length === 0 ? (
              <p className="text-sm text-muted-foreground">No topics identified</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {topTopicsList.map(([topic, count], index) => (
                  <Badge key={index} variant="secondary" className="gap-1">
                    {topic}
                    <span className="ml-1 text-xs text-muted-foreground">({count})</span>
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Recommendations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Top Recommendations
            </CardTitle>
            <CardDescription>AI-generated improvement suggestions</CardDescription>
          </CardHeader>
          <CardContent>
            {topRecommendationsList.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recommendations available</p>
            ) : (
              <ul className="space-y-2">
                {topRecommendationsList.map(([recommendation, count], index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Badge variant="outline" className="mt-0.5">
                      {count}
                    </Badge>
                    <span className="text-sm flex-1">{recommendation}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
